import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Control } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalibrationTaskFormSchema, PoType } from './zodSchemas';
import { TrashIcon } from '@radix-ui/react-icons';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ALGORITHM_LIST_OPTIONS, PO_TABLE_HEADERS } from './constants';
import { Cross2Icon, CheckIcon } from '@radix-ui/react-icons';

type PoTableProps = {
	items: PoType[];
	control: Control<CalibrationTaskFormSchema>;
	isLoading?: boolean;
	removePo: (index: number) => void;
};

const PoTable = (props: PoTableProps) => {
	const { items, control, removePo, isLoading } = props;

	return (
		<Table className={cn('w-full', { 'opacity-50 pointer-events-none': isLoading })}>
			<TableHeader>
				<TableRow>
					{Object.values(PO_TABLE_HEADERS).map(header => (
						<TableHead key={header}>{header}</TableHead>
					))}
				</TableRow>
			</TableHeader>
			<TableBody>
				{items.length === 0 && (
					<TableRow>
						<TableCell colSpan={10} className="text-center pt-10">
							No process objects available
						</TableCell>
					</TableRow>
				)}
				{items.map((po, index) => (
					<TableRow key={po.id}>
						<TableCell>
							<Button
								variant="outline"
								size="icon"
								type="button"
								title="Exclude from calibration task"
								className="px-2"
								onClick={() => removePo(index)}
							>
								<TrashIcon className="w-6 h-6 text-slate-900 dark:text-slate-100" />
							</Button>
						</TableCell>

						<TableCell>{po.name}</TableCell>
						<TableCell>
							<div title={po.latestKofFile ? 'kof file available' : 'kof file not available'}>
								{po.latestKofFile ? <CheckIcon /> : <Cross2Icon />}
							</div>
						</TableCell>
						<TableCell>{po.id}</TableCell>
						<TableCell>{po.msIds.join(', ')}</TableCell>
						<TableCell>{po.cx}</TableCell>
						<TableCell>{po.cy}</TableCell>
						<TableCell>{po.timeAdjustment}</TableCell>
						<TableCell>{po.kvClass}</TableCell>
						<TableCell>{po.primaryCalibrationFactor}</TableCell>
						<TableCell>
							<FormField
								control={control}
								name={`poList.${index}.ttmsIdPrimary`}
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<Input
												className="min-w-16"
												value={field.value}
												onChange={field.onChange}
												onBlur={field.onBlur}
												name={field.name}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</TableCell>
						<TableCell aria-label={po.algorithm.toString()}>
							<FormField
								control={control}
								name={`poList.${index}.algorithm`}
								render={({ field }) => (
									<FormItem>
										<Select onValueChange={field.onChange} value={field.value.toString()}>
											<FormControl>
												<SelectTrigger className="mb-0">
													<SelectValue />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{ALGORITHM_LIST_OPTIONS.map(algo => (
													<SelectItem
														key={algo.id}
														value={algo.id.toString()}
														disabled={algo.disabled}
													>
														{algo.id}
													</SelectItem>
												))}
											</SelectContent>
										</Select>

										<FormMessage />
									</FormItem>
								)}
							/>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
};

export default PoTable;
